import { inject } from '@angular/core';
import { Router, type CanMatchFn } from '@angular/router';
import { map } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Frontend-only enforcement of `mustChangePassword` (docs/adr/0014-*.md) — the backend gates no
 * route on this flag by design (docs/adr-be/0015-imported-credentials-and-forced-rotation.md), so
 * this `canMatch` is the sole thing standing between a seeded/temp password and it living forever.
 *
 * Wired in `app.routes.ts` on a single pathless wrapper around the shell's `designer`/`admin`
 * children and the voluntary `change-password` route — not on the shell route itself, so
 * `/set-password` (a direct shell child, outside this wrapper) can't block its own destination.
 * Also reused on `tracking-dispatch.guard.ts` so a printed-QR scan doesn't bypass it.
 *
 * Structured like `roleGuard`/`trackingDispatchGuard`: an immediate answer when the user is
 * already loaded, and a `bootstrap()` wait (single-flight) when a token exists but the user
 * hasn't loaded yet. Never returns bare `false` — a `canMatch` returning `false` falls through to
 * the `**` wildcard instead of redirecting anywhere sensible.
 */
export const mustChangePasswordGuard: CanMatchFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const gate = () => (auth.mustChangePassword() ? router.createUrlTree(['/set-password']) : true);

  if (auth.isAuthenticated()) {
    return gate();
  }

  if (auth.hasAccessToken()) {
    return auth.bootstrap().pipe(map((user) => (user ? gate() : router.createUrlTree(['/login']))));
  }

  return router.createUrlTree(['/login']);
};

/**
 * The inverse check, on `/set-password` itself: if a user reaches this route with
 * `mustChangePassword` already `false` (a stale bookmark, a link followed twice), send them to
 * the voluntary `/change-password` screen instead of rendering the forced one with nothing to
 * force.
 */
export const alreadyRotatedGuard: CanMatchFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const gate = () =>
    auth.mustChangePassword() ? true : router.createUrlTree(['/change-password']);

  if (auth.isAuthenticated()) {
    return gate();
  }

  if (auth.hasAccessToken()) {
    return auth.bootstrap().pipe(map((user) => (user ? gate() : router.createUrlTree(['/login']))));
  }

  return router.createUrlTree(['/login']);
};
